import React, { useCallback, useRef, useState } from 'react'
import { useOnViewportChange, useReactFlow, Viewport } from 'reactflow'

interface FlowchartScrollbarsProps {
  panExtent: [[number, number], [number, number]] | undefined
  containerRef: React.RefObject<HTMLDivElement | null>
}

const FlowchartScrollbars: React.FC<FlowchartScrollbarsProps> = ({ panExtent, containerRef }) => {
  const { getViewport, setViewport, flowToScreenPosition } = useReactFlow()
  const [viewport, setViewportState] = useState<Viewport>(
    () => getViewport?.() || { x: 0, y: 0, zoom: 1 }
  )
  useOnViewportChange({ onChange: setViewportState })
  const dragging = useRef<{ axis: 'x' | 'y' | null; startPos: number; startScroll: number }>({
    axis: null,
    startPos: 0,
    startScroll: 0,
  })

  const [[minX, minY], [maxX, maxY]] = panExtent || [
    [0, 0],
    [0, 0],
  ]
  const containerWidth = containerRef.current?.clientWidth || 0
  const containerHeight = containerRef.current?.clientHeight || 0
  const zoom = viewport.zoom || 1

  // Use flowToScreenPosition to get accurate thumb positions and sizes
  const graphTopLeftScreen = flowToScreenPosition
    ? flowToScreenPosition({ x: minX, y: minY })
    : { x: 0, y: 0 }
  const graphBottomRightScreen = flowToScreenPosition
    ? flowToScreenPosition({ x: maxX, y: maxY })
    : { x: containerWidth, y: containerHeight }
  const graphWidthScreen = graphBottomRightScreen.x - graphTopLeftScreen.x
  const graphHeightScreen = graphBottomRightScreen.y - graphTopLeftScreen.y

  const visibleWidth = containerWidth
  const visibleHeight = containerHeight

  const canScrollX = graphWidthScreen > visibleWidth + 1e-3
  const canScrollY = graphHeightScreen > visibleHeight + 1e-3

  const scrollableX = Math.max(1, graphWidthScreen - visibleWidth)
  const scrollableY = Math.max(1, graphHeightScreen - visibleHeight)

  const scrollX = Math.max(0, Math.min(scrollableX, graphTopLeftScreen.x * -1))
  const scrollY = Math.max(0, Math.min(scrollableY, graphTopLeftScreen.y * -1))

  const thumbWidth = Math.min(1, visibleWidth / graphWidthScreen) * containerWidth
  const thumbHeight = Math.min(1, visibleHeight / graphHeightScreen) * containerHeight

  const thumbLeft = scrollableX > 0 ? (scrollX / scrollableX) * (containerWidth - thumbWidth) : 0
  const thumbTop = scrollableY > 0 ? (scrollY / scrollableY) * (containerHeight - thumbHeight) : 0

  // Drag logic
  const onThumbMouseDown = useCallback(
    (axis: 'x' | 'y', e: React.MouseEvent) => {
      e.preventDefault()
      dragging.current.axis = axis
      dragging.current.startPos = axis === 'x' ? e.clientX : e.clientY
      dragging.current.startScroll = axis === 'x' ? scrollX : scrollY

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!containerRef.current) return
        const delta =
          (axis === 'x' ? moveEvent.clientX : moveEvent.clientY) - dragging.current.startPos
        if (axis === 'x') {
          const trackLength = containerWidth - thumbWidth
          const ratio = scrollableX / trackLength
          let newScroll = dragging.current.startScroll + delta * ratio
          newScroll = Math.max(0, Math.min(scrollableX, newScroll))
          // Convert scroll to viewport x
          const newX = -minX - newScroll
          setViewport({ x: newX, y: viewport.y, zoom })
        } else {
          const trackLength = containerHeight - thumbHeight
          const ratio = scrollableY / trackLength
          let newScroll = dragging.current.startScroll + delta * ratio
          newScroll = Math.max(0, Math.min(scrollableY, newScroll))
          // Convert scroll to viewport y
          const newY = -minY - newScroll
          setViewport({ x: viewport.x, y: newY, zoom })
        }
      }
      const onMouseUp = () => {
        dragging.current.axis = null
        window.removeEventListener('mousemove', onMouseMove)
        window.removeEventListener('mouseup', onMouseUp)
      }
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)
    },
    [
      containerRef,
      containerWidth,
      containerHeight,
      minX,
      minY,
      scrollX,
      scrollY,
      scrollableX,
      scrollableY,
      setViewport,
      thumbHeight,
      thumbWidth,
      viewport.x,
      viewport.y,
      zoom,
    ]
  )

  if (!panExtent || !containerRef.current) return null

  return (
    <>
      {canScrollX && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 2,
            height: 5,
            background: 'transparent',
            borderRadius: 9,
            pointerEvents: 'auto',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: thumbLeft,
              width: thumbWidth,
              height: 5,
              background: '#bdbdbd96',
              borderRadius: 9,
              transition: 'left 0.1s',
              cursor: 'pointer',
            }}
            onMouseDown={(e) => onThumbMouseDown('x', e)}
          />
        </div>
      )}
      {canScrollY && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 2,
            width: 5,
            background: 'transparent',
            borderRadius: 9,
            pointerEvents: 'auto',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: thumbTop,
              height: thumbHeight,
              width: 5,
              background: '#bdbdbd96',
              borderRadius: 9,
              transition: 'top 0.1s',
              cursor: 'pointer',
            }}
            onMouseDown={(e) => onThumbMouseDown('y', e)}
          />
        </div>
      )}
    </>
  )
}

export default FlowchartScrollbars
